import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, UserPlus, Users, X } from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  ProgressBar,
} from '@tutor/ui';
import { useRevokeInvitation, useSentInvitations, useStudents } from '../../hooks/queries';
import { InviteStudentDialog } from '../../components/dialogs/InviteStudentDialog';
import { formatDate, formatRelativeDayTime } from '../../lib/format';

const INVITATION_TONE = {
  PENDING: 'warning',
  ACCEPTED: 'success',
  DECLINED: 'neutral',
  REVOKED: 'neutral',
  EXPIRED: 'neutral',
} as const;

export function StudentsPage() {
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data, isLoading, isError } = useStudents({ search: search || undefined, page: 1 });
  const { data: invitations } = useSentInvitations();
  const revoke = useRevokeInvitation();

  const pending = invitations?.filter((invitation) => invitation.status === 'PENDING') ?? [];

  return (
    <>
      <PageHeader
        title="Students"
        description="Everyone you currently teach."
        action={
          <div className="flex items-center gap-2">
            <div className="w-56">
              <Input
                placeholder="Search students…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                leftIcon={<Search className="h-4 w-4" aria-hidden />}
                aria-label="Search students"
              />
            </div>
            <Button
              onClick={() => setInviteOpen(true)}
              leftIcon={<UserPlus className="h-4 w-4" aria-hidden />}
            >
              Invite student
            </Button>
          </div>
        }
      />

      <InviteStudentDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />

      {pending.length > 0 && (
        <Card className="mb-6">
          <CardHeader
            title="Pending invitations"
            description="These appear on the student's dashboard when they sign in."
          />
          <CardBody>
            <ul className="divide-y divide-slate-100">
              {pending.map((invitation) => (
                <li key={invitation.id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{invitation.email}</p>
                    <p className="text-xs text-slate-500">
                      Sent {formatDate(invitation.createdAt)}
                      {invitation.studentName ? ` · ${invitation.studentName}` : ' · no account yet'}
                    </p>
                  </div>
                  {invitation.subject && <Badge tone="brand">{invitation.subject.name}</Badge>}
                  <Badge tone={INVITATION_TONE[invitation.status]}>{invitation.status.toLowerCase()}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Revoke invitation to ${invitation.email}`}
                    onClick={() => revoke.mutate(invitation.id)}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState description="We could not load your students." />
      ) : (data?.items.length ?? 0) === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              title={search ? 'No students match that search' : 'No students yet'}
              description={search ? undefined : 'Invite a student by email to get started.'}
              icon={<Users className="h-5 w-5" aria-hidden />}
              action={
                search ? undefined : (
                  <Button onClick={() => setInviteOpen(true)} leftIcon={<UserPlus className="h-4 w-4" aria-hidden />}>
                    Invite student
                  </Button>
                )
              }
            />
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data!.items.map((student) => (
            <Link
              key={student.id}
              to={`/tutor/students/${student.id}`}
              className="surface block p-5 transition-colors hover:border-brand-300"
            >
              <div className="flex items-start gap-3">
                <Avatar name={student.user.fullName} src={student.user.avatarUrl} size="lg" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-900">{student.user.fullName}</p>
                  <p className="truncate text-sm text-slate-500">
                    {student.profile?.yearGroup ?? '—'}
                    {student.profile?.school && ` · ${student.profile.school}`}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {student.subjects.map((subject) => (
                  <Badge key={subject.id} tone="brand">
                    {subject.name}
                  </Badge>
                ))}
              </div>

              {student.averageProgress !== null && (
                <div className="mt-4">
                  <ProgressBar label="Average progress" value={student.averageProgress} size="sm" />
                </div>
              )}

              <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-xs">
                <div>
                  <dt className="text-slate-500">Next lesson</dt>
                  <dd className="mt-0.5 font-medium text-slate-900">
                    {student.nextLessonAt ? formatRelativeDayTime(student.nextLessonAt) : 'Not scheduled'}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Homework out</dt>
                  <dd className="mt-0.5 font-medium text-slate-900">{student.homeworkOutstanding}</dd>
                </div>
              </dl>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
